import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

/** Best-effort: claim a one-shot localStorage code by POSTing to a backend route.
 * Returns `true` only if the backend confirmed acceptance (used to redirect to /settings). */
async function claimPendingCode(storageKey, endpoint) {
  let accepted = false;
  try {
    const code = window.localStorage.getItem(storageKey);
    if (!code) return false;
    try {
      await api.post(endpoint, { code });
      accepted = true;
    } catch (err) {
      // 4xx (invalid/expired/already-claimed) — log for debug, swallow for UX
      console.warn(`[auth] ${endpoint} failed:`, err?.response?.status, err?.message);
    }
    window.localStorage.removeItem(storageKey);
  } catch (err) {
    // localStorage unavailable (private browsing on Safari, etc.) — non-fatal
    console.warn(`[auth] localStorage[${storageKey}] unavailable:`, err?.message);
  }
  return accepted;
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sid) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sid });
        // Clear hash from URL
        window.history.replaceState(null, "", window.location.pathname);

        // CRITICAL: hydrate AuthContext synchronously with the freshly authenticated
        // user. Without this, AuthContext.user remains null (its mount-time `/me`
        // check was skipped because the hash was present), and ProtectedRoute
        // bounces the user back to /login despite the session cookie being set.
        setUser(data);

        // Claim any pending referral / household-invite code captured on Landing
        await claimPendingCode("lf_pending_ref", "/referrals/claim");
        const acceptedHousehold = await claimPendingCode(
          "lf_pending_invite",
          "/households/accept"
        );

        // Decide landing route
        if (acceptedHousehold) {
          navigate("/settings?tab=household", { replace: true, state: { user: data } });
          return;
        }
        try {
          const { data: profile } = await api.get("/profile");
          if (!profile?.onboarding_completed) {
            navigate("/onboarding", { replace: true, state: { user: data } });
            return;
          }
        } catch (err) {
          // Profile fetch failed — fall through to /dashboard which will retry.
          console.warn("[auth] profile fetch failed:", err?.message);
        }
        navigate("/dashboard", { replace: true, state: { user: data } });
      } catch (e) {
        console.error("[auth] session exchange failed:", e);
        setError(e.message || "Auth failed");
      }
    })();
    // navigate is stable; intentionally empty closure for one-shot exchange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-900" data-testid="auth-callback">
      <div className="text-center">
        {error ? (
          <>
            <p className="text-red-600 font-medium" data-testid="auth-callback-error">{error}</p>
            <a href="/login" className="text-blue-700 underline text-sm mt-4 inline-block">Retour à la connexion</a>
          </>
        ) : (
          <>
            <div className="w-10 h-10 border-2 border-slate-200 border-t-blue-700 rounded-full animate-spin mx-auto" />
            <p className="text-slate-600 dark:text-slate-300 mt-4 text-sm">Connexion en cours…</p>
          </>
        )}
      </div>
    </div>
  );
}
